import { FormEventHandler, useEffect, useMemo } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  FieldErrors,
  SubmitHandler,
  UseFormReturn,
  useForm,
} from "react-hook-form";
import { Toast } from "@sscs/ui";
import { PERMISSION } from "Constants";
import { useAllowedTo, useCurrentOrganization } from "Hooks";
import { apiErrorToast } from "Utils";
import {
  useOidcGroupMappingsQuery,
  useUpdateOidcGroupMappingsMutation,
} from "../services/sso.api";
import { GroupMappingFormData } from "../types";
import { GroupMappingFormSchema } from "../utils";

type UseGroupMappingForm = {
  errors: FieldErrors<GroupMappingFormData>;
  isSaveDisabled: boolean;
  onSubmit: FormEventHandler<HTMLFormElement>;
  methods: UseFormReturn<GroupMappingFormData>;
  isLoading: boolean;
};

const EMPTY_GROUP_MAPPINGS = {
  group_mappings: [{ group_id: "", mapping: "" }],
};

const useGroupMappingForm = (): UseGroupMappingForm => {
  const currentOrganization = useCurrentOrganization();

  // this additional permissions check helps skip api calls when someone tries to access a
  // page they don't have natural access to
  const isAdmin = useAllowedTo({
    permissions: [PERMISSION.SETUP_SSO],
  });

  // get the current list of group mappings
  const { data = EMPTY_GROUP_MAPPINGS, isLoading } = useOidcGroupMappingsQuery(
    {
      orgId: currentOrganization?.id || "",
    },
    { skip: !currentOrganization || !isAdmin },
  );

  const defaultValues = useMemo(() => {
    return data.group_mappings.length > 0 ? data : EMPTY_GROUP_MAPPINGS;
  }, [data]);

  // update the list of group mappings
  const [updateGroupMappings] = useUpdateOidcGroupMappingsMutation();

  const methods = useForm<GroupMappingFormData>({
    resolver: yupResolver(GroupMappingFormSchema),
    defaultValues,
  });

  const {
    formState: { errors, isDirty },
    reset,
    handleSubmit,
  } = methods;

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  const onSubmit: SubmitHandler<GroupMappingFormData> = async (
    data: GroupMappingFormData,
  ) => {
    try {
      if (currentOrganization) {
        await updateGroupMappings({
          body: {
            organization_id: currentOrganization?.id,
            // we allow FULLY blank rows in the form for better UX, but we need to filter them out here
            group_mappings: data?.group_mappings.filter(
              (groupMapping) => groupMapping.mapping && groupMapping.group_id,
            ),
          },
        }).unwrap();

        Toast.show({
          title: "Group mappings updated",
          type: "success",
        });
      }
    } catch (error) {
      apiErrorToast(error, "Could not update policy");
    }
  };

  return {
    onSubmit: handleSubmit(onSubmit),
    isSaveDisabled: !isDirty,
    errors,
    methods,
    isLoading,
  };
};

export default useGroupMappingForm;
